import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import {
  type ApplyRequest, entityToApplyRequest, applyRequestToEntity,
  APPLY_REQUEST_PK, APPLY_REQUEST_RK,
} from "@m8t-stack/platform-release";
import { discoverStampStorage } from "./platform-storage-discovery.js";

export async function openApplyTable(opts: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }): Promise<TableClient> {
  const { tableEndpoint } = await discoverStampStorage(opts);
  return new TableClient(tableEndpoint, "Metadata", opts.credential);
}

interface Row { value: string; etag: string }

function hasStatus(e: unknown, n: number): boolean {
  return (e as { statusCode?: number }).statusCode === n;
}

/**
 * Read the apply-request row. Swallows ONLY a 404 (row absent) → `null`;
 * anything else (esp. 403 — e.g. a Managed Identity missing "Storage Table
 * Data Contributor") rethrows, so an access failure surfaces loudly instead
 * of silently reading as "no request pending".
 */
export async function readApplyRequest(client: TableClient): Promise<(ApplyRequest & { etag: string }) | null> {
  try {
    const row = (await client.getEntity(APPLY_REQUEST_PK, APPLY_REQUEST_RK)) as unknown as Row;
    return { ...entityToApplyRequest(row), etag: row.etag };
  } catch (e) {
    if (hasStatus(e, 404)) return null;
    throw e;
  }
}

/**
 * pending → claimed, ETag-guarded. Returns the claimed row, or null if not
 * claimable / lost the race.
 *
 * Also reclaims an ABANDONED `claimed` row once its lease has expired: if a
 * prior execution crashed (hard process kill — no time to run any catch)
 * between claiming and writing a terminal status, the row would otherwise sit
 * at `status:"claimed"` forever, since nothing else ever reads `leaseUntil`.
 * A row is claimable when it is `pending`, it is `awaiting-engine-update`
 * (the self-update hop parked it there deliberately so the NEXT tick — now
 * running the updated engine — re-claims and proceeds; see converge.ts step 4.
 * That next tick runs the target release's installer image, whose baked
 * installer release EQUALS the target (and whose baked CLI_VERSION is ≥ that
 * release's cli.min) by construction, so `needsEngineUpdate` returns false and
 * it proceeds to apply — no infinite hop), OR it is `claimed` AND its
 * `leaseUntil` is strictly in the past
 * relative to `nowIso` (both plain ISO strings — no reliance on the machine
 * clock, just the two stamps already on the wire). A `claimed` row with a
 * still-live lease is left alone: a healthy in-flight execution owns it.
 */
export async function claimApplyRequest(client: TableClient, executionId: string, nowIso: string, leaseMs: number): Promise<ApplyRequest | null> {
  const cur = await readApplyRequest(client);
  if (!cur) return null;
  const leaseExpired = cur.status === "claimed" && cur.leaseUntil !== null && Date.parse(cur.leaseUntil) < Date.parse(nowIso);
  const claimable = cur.status === "pending" || cur.status === "awaiting-engine-update" || leaseExpired;
  if (!claimable) return null;
  const nowMs = Date.parse(nowIso);
  const leaseUntil = new Date((Number.isNaN(nowMs) ? Date.now() : nowMs) + leaseMs).toISOString();
  const claimed: ApplyRequest = { ...cur, status: "claimed", claimedBy: executionId, leaseUntil, updatedAt: nowIso };
  try {
    await client.updateEntity({ ...applyRequestToEntity(claimed), etag: cur.etag } as never, "Merge", { etag: cur.etag });
    return claimed;
  } catch (e) {
    // 412 precondition failed → another execution claimed it first; anything
    // else (esp. 403) is a real failure and must not read as "lost the race".
    if (hasStatus(e, 412)) return null;
    throw e;
  }
}

/**
 * ETag read-modify-write for a status/phase transition. `readApplyRequest`
 * only returns null on a 404, so the early-return here is a genuine
 * "no request row" no-op. The `updateEntity` write is deliberately
 * unguarded: the job is the sole writer post-claim (parallelism 1), so a
 * failure here is a real bug that should surface loudly, not be swallowed.
 */
export async function patchApplyRequest(client: TableClient, mutate: (r: ApplyRequest) => ApplyRequest): Promise<void> {
  const cur = await readApplyRequest(client);
  if (!cur) return;
  const next = mutate(cur);
  await client.updateEntity({ ...applyRequestToEntity(next), etag: cur.etag } as never, "Merge", { etag: cur.etag });
}
